<script setup>
import { ref, onMounted } from 'vue'
import PanelOrganic from '@/design/primitives/PanelOrganic.vue'
import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import CoinGlyph from '@/design/primitives/CoinGlyph.vue'
import { api } from '@/api/client.js'
import { EP } from '@/api/endpoints.js'

const orders  = ref([])
const loading = ref(false)
const error   = ref(null)
let   fetched = false

async function load() {
  if (fetched) return
  loading.value = true
  error.value   = null
  try {
    const res = await api.get(EP.userOrders)
    orders.value = Array.isArray(res.data) ? res.data : []
    fetched = true
  } catch (e) {
    error.value = e.message || 'Could not load orders.'
  } finally {
    loading.value = false
  }
}

function formatDate(raw) {
  if (!raw) return '—'
  return new Date(raw).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  })
}

function statusClass(status) {
  if (!status) return ''
  const s = status.toLowerCase().replace(/\s+/g, '-')
  return `badge--${s}`
}

// Surface the DELIVERY state (like arkworlds), not just payment: an order stays
// "Pending" until the item actually reaches the game (the auto-delivery worker
// runs every ~5 min once you're online), then flips to "Delivered". order.status
// only tracks payment, so a paid-but-undelivered order must NOT read "completed".
function effectiveOrderStatus(order) {
  if (order.status === 'refunded') return 'refunded'
  const d = (order.deliveryStatus || '').toLowerCase()
  if (d === 'delivered') return 'delivered'
  if (d === 'pending')   return 'pending'
  if (d === 'failed')    return 'failed'
  // not_applicable / no delivery → reflect the order status (e.g. balance-only)
  return order.status || 'completed'
}
function orderStatusLabel(order) {
  const s = effectiveOrderStatus(order)
  const map = { delivered: 'Delivered', pending: 'Pending', failed: 'Failed', refunded: 'Refunded', completed: 'Completed' }
  return map[s] || s
}

onMounted(load)
</script>

<template>
  <section class="dossier-section" aria-labelledby="orders-heading">
    <h2 id="orders-heading" class="stamp u-display u-allcaps">Orders</h2>

    <PanelOrganic tone="strong" :padded="true">
      <!-- Loading -->
      <div v-if="loading" class="ledger-state" aria-live="polite">
        <span class="dossier-spinner" aria-hidden="true" />
        <span class="ledger-state__text u-display u-allcaps">Loading orders…</span>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="ledger-state ledger-state--err" aria-live="assertive">
        <span class="ledger-state__text">{{ error }}</span>
        <ButtonSeed variant="ghost" size="sm" @click="() => { fetched = false; load() }">RETRY</ButtonSeed>
      </div>

      <!-- Empty -->
      <div v-else-if="!orders.length" class="ledger-state ledger-state--empty">
        <span class="u-display u-allcaps">No orders yet</span>
      </div>

      <!-- Ledger -->
      <ul v-else class="ledger" role="list" aria-label="Orders">
        <li class="ledger__header" aria-hidden="true">
          <span class="ledger__col ledger__col--date u-display u-allcaps">Date</span>
          <span class="ledger__col ledger__col--name u-display u-allcaps">Product</span>
          <span class="ledger__col ledger__col--qty  u-display u-allcaps">Qty</span>
          <span class="ledger__col ledger__col--status u-display u-allcaps">Status</span>
          <span class="ledger__col ledger__col--total u-display u-allcaps">Total</span>
        </li>

        <li
          v-for="order in orders"
          :key="order.orderId ?? order.id"
          class="ledger__row"
        >
          <span class="ledger__col ledger__col--date u-mono">{{ formatDate(order.createdAt) }}</span>
          <span class="ledger__col ledger__col--name u-display u-allcaps ledger__product">
            {{ order.productName || order.orderId }}
          </span>
          <span class="ledger__col ledger__col--qty u-mono">
            {{ order.quantity ?? '—' }}
          </span>
          <span
            class="ledger__col ledger__col--status ledger__badge"
            :class="statusClass(effectiveOrderStatus(order))"
          >
            {{ orderStatusLabel(order) }}
          </span>
          <div class="ledger__col ledger__col--total">
            <CoinGlyph :size="14" />
            <span class="u-mono u-gold">
              {{ (parseFloat(order.totalAmount ?? order.totalPrice ?? order.price) || 0).toFixed(2) }}
            </span>
          </div>

          <p
            v-if="effectiveOrderStatus(order) === 'pending'"
            class="ledger__note"
          >
            Start the game and join the server to receive your items.
          </p>
        </li>
      </ul>
    </PanelOrganic>
  </section>
</template>

<style scoped>
.dossier-section {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.stamp {
  font-size: clamp(var(--fs-21), 2.5vw, var(--fs-32));
  color: var(--gold);
  letter-spacing: 0.12em;
  margin: 0;
}

/* Ledger */
.ledger {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.ledger__header,
.ledger__row {
  display: grid;
  grid-template-columns: 100px 1fr 60px 110px 120px;
  align-items: center;
  gap: var(--sp-4);
  padding: var(--sp-3) var(--sp-4);
}

.ledger__header {
  border-bottom: 1px solid var(--ash);
  margin-bottom: var(--sp-1);
}

.ledger__header .ledger__col {
  font-size: var(--fs-11, 0.6875rem);
  color: var(--ink-mute);
  letter-spacing: 0.14em;
}

.ledger__row {
  border-bottom: 1px solid var(--ash);
  transition: background var(--t-fast) var(--ease-cinema);
}
.ledger__row:last-child { border-bottom: none; }
.ledger__row:hover { background: rgba(229, 199, 107, 0.04); }

.ledger__col { min-width: 0; font-size: var(--fs-14); color: var(--ink-soft); }

.ledger__product {
  font-size: var(--fs-14);
  color: var(--ink);
  letter-spacing: 0.06em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ledger__col--total {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ledger__badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: var(--r-xs);
  font-family: var(--font-display);
  font-size: var(--fs-11, 0.6875rem);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  white-space: nowrap;
  border: 1px solid var(--ash);
  background: var(--bg-deep);
  color: var(--ink-mute);
}
.badge--delivered,
.badge--completed  { background: var(--gold-soft); color: var(--gold); border-color: rgba(229, 199, 107, 0.3); }
.badge--pending    { background: rgba(184, 90, 58, 0.12); color: var(--copper); border-color: rgba(184, 90, 58, 0.3); }
.badge--failed,
.badge--refunded   { background: rgba(230, 76, 43, 0.12); color: var(--ember); border-color: rgba(230, 76, 43, 0.3); }

/* Pending delivery hint — spans the full row width below the grid columns */
.ledger__note {
  grid-column: 1 / -1;
  margin: var(--sp-1) 0 0;
  font-size: var(--fs-12);
  color: var(--ink-mute);
  letter-spacing: 0.02em;
}

/* States */
.ledger-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-3);
  padding: var(--sp-12) var(--sp-4);
  color: var(--ink-soft);
}
.ledger-state--err   { color: var(--ember); flex-direction: column; }
.ledger-state--empty { color: var(--ink-mute); }
.ledger-state__text  { font-size: var(--fs-14); }

.dossier-spinner {
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 2px solid var(--ash);
  border-top-color: var(--gold);
  border-radius: 50%;
  animation: spin 1.2s linear infinite;
  flex-shrink: 0;
}
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 700px) {
  .ledger__header,
  .ledger__row {
    grid-template-columns: 90px 1fr 90px 100px;
  }
  .ledger__col--qty { display: none; }
}

@media (max-width: 500px) {
  .ledger__header,
  .ledger__row {
    grid-template-columns: 1fr 90px 100px;
  }
  .ledger__col--date { display: none; }
}
</style>
